import { Suspense, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Loading } from '../ui';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import TabBar from './TabBar';
import ChatDock from './ChatDock';
import CopilotDrawer from './CopilotDrawer';
import AgentNavigationBridge from './AgentNavigationBridge';
import PageErrorBoundary from './PageErrorBoundary';
import AgentGlobalActions from '../copilot/AgentGlobalActions';
import { CopilotProvider } from '../../api/copilot';
import TimeSeriesModal from './TimeSeriesModal';
import Toast from './Toast';
import JobToasts from '../jobs/JobToasts';
import { BackgroundJobsProvider } from '../../api/backgroundJobs';
import { setAssignedRole, setClient, setSidebarOpen } from '../../state/uiSlice';
import { resolveSessionRole } from '../../api/session';
import { useEmployeeSeat } from '../../api/hooks';
import {
  INDIVIDUAL_SEAT, PROFILES, ROUTES, isHomePath, isPathAllowed,
} from '../../config/navigation';
import { useDismissable } from '../../hooks';
import '../../theme/design.css';

/**
 * App shell: the design's `.app` grid — sticky sidebar, glass topbar, the
 * screen, then the persistent Copilot dock and overlays.
 *
 * Theme and palette are written onto <html> by App, not here — the marketing
 * and auth screens render outside this shell and share the same setting.
 */
export default function AppLayout() {
  const dispatch = useDispatch();
  const { sidebarOpen, sidebarMini, chatOpen, chatWidth, assignedRole } = useSelector((s) => s.ui);
  const auth = useSelector((s) => s.auth);
  const { pathname } = useLocation();

  /* Resolve the seat this account holds — from the token if it carries a role
     claim, otherwise from what was chosen at signup.

     The staff seat is the one exception: the token grants it, but the console
     belongs to Keep Investing's own people, so an employee token carrying
     another firm's membership falls back to the distributor's screens. The
     wait on `loading` is deliberate — the organization lands a moment after
     the token, and deciding early would flash the wrong seat on every
     reload. */
  const employeeSeat = useEmployeeSeat();
  useEffect(() => {
    const { assigned } = resolveSessionRole({ token: auth.token, userId: auth.userId });
    if (assigned === 'employee') {
      if (employeeSeat.loading) return;
      dispatch(setAssignedRole(employeeSeat.allowed ? 'employee' : 'mfd'));
      return;
    }
    dispatch(setAssignedRole(assigned));
  }, [auth.token, auth.userId, employeeSeat.loading, employeeSeat.allowed, dispatch]);
  /* An individual's screens work client by client with themselves as the
     client, so the remembered client is their own id while they are in that
     seat — Goals, the planner, trading accounts and proposals open on them. */
  const role = useSelector((s) => s.ui.role);
  useEffect(() => {
    if (role === INDIVIDUAL_SEAT && auth.userId) dispatch(setClient(String(auth.userId)));
  }, [role, auth.userId, dispatch]);
  const shellRef = useRef(null);
  const closeSidebar = useCallback(() => dispatch(setSidebarOpen(false)), [dispatch]);
  /* The off-canvas sidebar lives inside the shell, so Escape is what the hook
     adds here — the scrim below already handles the tap-outside case. */
  useDismissable(sidebarOpen, shellRef, closeSidebar);

  /* A role only reaches its own screens. Without an assigned role the app stays
     in the design's test-profile mode, where every seat is previewable. A seat
     with no known landing is left where it is rather than sent to `undefined`. */
  const landing = assignedRole && !isPathAllowed(assignedRole, pathname)
    ? ROUTES[PROFILES[assignedRole]?.landing] ?? null
    : null;

  /* On a landing screen the assistant is in the page, centred under the
     greeting, so the side column stands down there: the shell keeps two
     columns and the dock stays collapsed. `chatOpen` itself is untouched, so
     a column left open comes back on the next screen. */
  const home = isHomePath(pathname);
  const dockOpen = chatOpen && !home;
  /* The Alpha Desk keeps the suite's sidebar and top bar; its pages are
     full-bleed, so the page gutter is dropped under /desk. */
  const desk = pathname === '/desk' || pathname.startsWith('/desk/');

  return (
    /* The copilot connects here rather than at the app root because minting its
       token needs a session: mounted any higher it would fire on the login
       screen and be refused. One provider for both the dock and the drawer —
       they are two views of one conversation, and a room each would also be
       an agent session each. The voice state lives inside those two panels
       and the top bar's button; there is deliberately no floating overlay.

       It is the outermost element in EVERY branch, including the seat-guard
       redirect below. When the guard returned its <Navigate> in place of the
       provider, a disallowed URL on first load (the seat resolves a tick after
       mount) — or an agent navigation the guard bounced — unmounted the
       provider, dropped the room mid-turn, and minted a fresh token on the way
       back. Rendered inside it, a redirect only swaps the shell. */
    <CopilotProvider>
      {/* Background jobs follow the reader across every screen, so the one
          poller lives here with the session — and, like the copilot, outside
          the seat-guard branch, so a redirect does not restart it. */}
      <BackgroundJobsProvider>
      {/* Both render nothing and sit ahead of the branch, so neither remounts
          when the guard redirects. The bridge hands the router to the agent's
          navigation code; the global actions answer agent requests that
          belong to no screen (the watchlist). */}
      <AgentNavigationBridge />
      <AgentGlobalActions />

      {/* `chat` widens the shell to a third column for the copilot, so the
          page is narrowed by the panel rather than covered by it, and
          `--chat-w` is how wide that column is — the one value the panel, its
          rows and the resize handle all read. */}
      {landing ? (
        <Navigate to={landing} replace />
      ) : (
        <div
          className={['app', sidebarMini && 'mini', dockOpen && 'chat'].filter(Boolean).join(' ')}
          style={{ '--chat-w': `${chatWidth}px` }}
          ref={shellRef}
        >
          <Sidebar />
          <div className="main">
            <Topbar />
            <div className={desk ? 'wrap wrap-desk' : 'wrap'}>
              {/* The boundary belongs here, not around the router.
                  Pages are lazy, so the first visit to one suspends — and a
                  boundary above the shell would swap the sidebar, the top bar
                  and the copilot panel for the fallback and then rebuild
                  them, which is a full-screen flash on a single-page app and
                  a fresh agent room every time. Nested here, only the page
                  area waits. The error boundary inside it is there for the
                  same reason: a page that throws, or whose chunk is gone
                  after a deploy, replaces only the page area. */}
              <Suspense fallback={<Loading rows={4} label="Loading…" />}>
                <PageErrorBoundary resetKey={pathname}>
                  <Outlet />
                </PageErrorBoundary>
              </Suspense>
            </div>
          </div>

          {/* Mobile: tapping the scrim closes the off-canvas sidebar. */}
          <div
            className={sidebarOpen ? 'scrim on' : 'scrim'}
            onClick={closeSidebar}
          />

          <TabBar />
          <ChatDock hidden={home} />
          <CopilotDrawer />
          <TimeSeriesModal />
          <Toast />
          <JobToasts />
        </div>
      )}
      </BackgroundJobsProvider>
    </CopilotProvider>
  );
}
